import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useMutation } from "react-query";
import { Dialog, Transition } from "@headlessui/react";
import { useHistory, useParams } from "react-router-dom";
import { ErrorMessage, Field, FieldArray, Formik } from "formik";
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';

import UTILS from "../../utils";
import Fields from "../formFields";
import { addStudents } from "../../api/students";
import Partials from "../partials";
import PropTypes from "prop-types";

const phoneInputStyles = `
  .react-tel-input {
    font-family: inherit;
    width: 100%;
    position: relative;
  }
  
  .rowWrapper {
  overflow: visible !important;
}.BlockAddStudentInClass .react-tel-input .country-list {
  position: fixed !important;
  z-index: 9999 !important;
}
  
  .react-tel-input .form-control {
    width: 100% !important;
    height: 52px !important;
    padding: 6px 6px 6px 48px !important;
    font-size: 14px !important;
    border: 1px solid #d1d5db !important;
    border-radius: 6px !important;
    line-height: 1.5 !important;
    background: #fff;
  }
  
  .react-tel-input .flag-dropdown {
    border: 1px solid #d1d5db !important;
    border-radius: 6px 0 0 6px !important;
    background-color: #f9fafb !important;
  }
  
  .react-tel-input .selected-flag {
    width: 38px !important;
    padding: 0 0 0 8px !important;
    border-radius: 6px 0 0 6px !important;
  }
  
  .react-tel-input .selected-flag .arrow {
    left: 20px !important;
    border-top: 4px solid #555 !important;
  }
  
  .react-tel-input .country-list {
    width: 300px !important;
    font-size: 14px !important;
    margin-top: 1px !important;
    border-radius: 6px !important;
    box-shadow: 0 2px 8px rgba(0,0,0,0.1) !important;
    z-index: 9999 !important;
  }
  
  .react-tel-input .country:hover {
    background-color: #f5f5f5 !important;
  }
  
  .react-tel-input .highlight {
    background-color: #f5f5f5 !important;
  }
  
  .react-tel-input .search {
    padding: 8px !important;
    border-bottom: 1px solid #eee !important;
  }
  
  .react-tel-input .search input {
    width: 100% !important;
    padding: 6px 10px !important;
    border: 1px solid #d1d5db !important;
    border-radius: 4px !important;
    font-size: 14px !important;
  }
  
  /* Modal specific styles */
  .modal-phone-input .react-tel-input .country-list {
    z-index: 99999 !important;
    position: fixed !important;
    top: auto !important;
    left: auto !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }
`;

const defaultValue = [
  {
    first_name: "",
    last_name: "",
    mobile_number: "",
    email: "",
    verified: false,
  },
];

// eslint-disable-next-line react/display-name
const BlockAddStudentsToClass = forwardRef(({ initialData, isEdit }, ref) => {
  const arrayHelpersRef = useRef(null);
  const listEndRef = useRef(null);
  const phoneInputRef = useRef(null);
  const modalPhoneInputRef = useRef(null);
  const [editingStudent, _seEditingStudent] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const [initialValues, _setInitialValues] = useState({
    students: defaultValue,
  });

  const history = useHistory();
  const params = useParams();
  const classId = params.classId;

  useImperativeHandle(ref, () => ({
    setExcelData: setExcelData,
  }));

  const scrollToBottom = (listId) => {
    setTimeout(() => {
      const element = document.getElementById(`student-list-${listId}`);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }, 100);
  };

  useEffect(() => {
    if (!classId) {
      history.replace("/create-virtual-class");
    }
  });

  const { isLoading, error, isError, mutate, data } = useMutation(addStudents, {
    onSuccess: (data) => {
      history.push(`/class/${classId}/invite-students`);
    },
    onError: (err) => {
      console.log(err);
    },
  });

  const addRow = (arrayHelpers) => {
    const currentVisibleList =
      arrayHelpers?.form?.values?.students.length + 1 || 1;
    arrayHelpers.push({
      first_name: "",
      last_name: "",
      mobile_number: "",
      email: "",
      verified: false,
    });
    scrollToBottom(currentVisibleList);
  };

  const setExcelData = function (data, reset = false) {
    if (reset) {
      const dataValue = data?.map((item) => {
        return {
          ...item,
          mobile_number: item.mobile_number.substring(
            item.mobile_number.indexOf("-") + 1
          ),
        };
      });
      if (dataValue?.length > 0) {
        _setInitialValues({ students: dataValue });
      } else {
        _setInitialValues({ students: data });
      }
    } else {
      data?.map((student) => {
        arrayHelpersRef.current.push(student);
      });
    }
  };

  const removeRow = (arrayHelpers, index, values) => {
    if (values.students.length > 1) {
      arrayHelpers.remove(index);
    }
  };

  const handleSubmit = (values, { setSubmitting }) => {
    setSubmitting(false);
    mutate({ classId, students: values.students });
  };

  const handleModalSubmit = (values, { setSubmitting }) => {
    const existingStudents = arrayHelpersRef.current.form.values.students;

    const filterStudents = existingStudents.filter(
      (item) => item.id !== values.id
    );
    const updateStudentPayload = [...filterStudents, values];
    if (updateStudentPayload.length) {
      mutate({ classId, students: updateStudentPayload });
      setSubmitting(false);
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    _seEditingStudent({});
  };

  const openModal = (student) => {
    setIsOpen(true);
    _seEditingStudent(student);
  };

  const handlePhoneChange = (value, index, setFieldValue) => {
    setFieldValue(`students.${index}.mobile_number`, value);
  };

  const handleKeyDown = (e, inputRef) => {
  if (e.key === 'Backspace') {
    const input = e.target;
    const cursorPosition = input.selectionStart;
    const formattedValue = input.value;
    const countryDialCode = inputRef?.current?.state?.country?.dialCode || '';
    const countryCodeLength = countryDialCode.length;
    
    let phoneNumberStart = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        phoneNumberStart = i;
        break;
      }
    }

    const numericValueBeforeCursor = formattedValue
      .slice(0, cursorPosition)
      .replace(/\D/g, '');

    if (numericValueBeforeCursor.length <= countryCodeLength) {
      e.preventDefault();
      return;
    }

    if (cursorPosition > 0 && !/\d/.test(formattedValue[cursorPosition - 1])) {
      e.preventDefault();
      return;
    }
  }
};

  return (
    <>
      <style>{phoneInputStyles}</style>
      
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={UTILS.forms.AddStudentsToClassSchema}
        onSubmit={handleSubmit}
      >
        {({ values, handleSubmit, isSubmitting, setFieldValue }) => (
          <form
            onSubmit={handleSubmit}
            noValidate
            className="pt-2 md:pt-6 pb-0 md:pb-2"
          >
            <FieldArray
              name="students"
              render={(arrayHelpers) => {
                const students = values.students;
                arrayHelpersRef.current = arrayHelpers;
                return (
                  <>
                    <div className={"hidden md:grid md:grid-cols-10 mb-4 md:gap-2"}>
                      <div className={"font-['Poppins-Bold']"}>Sr. No</div>
                      <div className={"font-['Poppins-Bold'] col-span-2"}>
                        First name
                      </div>
                      <div className={"font-['Poppins-Bold'] col-span-2"}>
                        Last name
                      </div>
                      <div className={"font-['Poppins-Bold'] col-span-2"}>
                        Mobile number
                      </div>
                      <div className={"font-['Poppins-Bold'] col-span-2"}>
                        Email Id
                      </div>
                      {isEdit && (
                        <div className={"font-['Poppins-Bold']"}></div>
                      )}
                    </div>
                    <div
                      className="BlockAddStudentInClass overflow-y-auto max-h-[55vh] pr-1 pl-2"
                      ref={listEndRef}
                    >
                      {students.map((student, index) => (
                        <Transition
                          as={Fragment}
                          show={index > -1}
                          key={"add-students-to-class" + index}
                          enter="transform transition duration-[400ms]"
                          enterFrom="opacity-0 scale-90"
                          enterTo="opacity-100 scale-100"
                          id={`student-list-${index + 1}`}
                        >
                          <div className="grid grid-cols-10 gap-2 md:gap-2 border-b lg:border-b-0 rowWrapper">
                            <div
                              className={
                                "md:hidden col-span-12 w-10 h-12 bg-white shadow rounded-lg flex justify-center items-center text-md mb-0 col-auto"
                              }
                            >
                              <button
                                type={"button"}
                                className={"pt-2 px-1 rotate-45"}
                                onClick={() =>
                                  removeRow(arrayHelpers, index, values)
                                }
                              >
                                <i className="icon icon-plus-circle text-2xl"></i>
                              </button>
                            </div>
                            <div
                              className={
                                "hidden w-10 h-12 bg-white shadow rounded-lg md:flex justify-center items-center text-md mb-0 col-auto"
                              }
                            >
                              <button
                                type={"button"}
                                className={"pt-2 px-1 rotate-45"}
                                onClick={() =>
                                  removeRow(arrayHelpers, index, values)
                                }
                              >
                                <i className="icon icon-plus-circle text-2xl rotate-45"></i>
                              </button>
                            </div>
                            <div
                              className={
                                "col-start-1 md:col-start-0 col-span-10 sm:col-span-5 md:col-span-2"
                              }
                            >
                              <Field
                                type={"text"}
                                name={`students.${index}.first_name`}
                                wrapperClasses={"-mb-0"}
                                placeholder="First Name"
                                component={Fields.FieldInput}
                              />
                              <ErrorMessage
                                name={`students.${index}.first_name`}
                              >
                                {(msg) => (
                                  <div className={"text-red-500 text-xs"}>
                                    {msg}
                                  </div>
                                )}
                              </ErrorMessage>
                            </div>
                            <div
                              className={
                                "col-start-1 md:col-start-0 col-span-10 sm:col-span-5 md:col-span-2"
                              }
                            >
                              <Field
                                type={"text"}
                                name={`students.${index}.last_name`}
                                wrapperClasses={"-mb-0"}
                                placeholder="Last Name"
                                component={Fields.FieldInput}
                              />
                              <ErrorMessage
                                name={`students.${index}.last_name`}
                              >
                                {(msg) => (
                                  <div className={"text-red-500 text-xs"}>
                                    {msg}
                                  </div>
                                )}
                              </ErrorMessage>
                            </div>
                            <div
                              className={
                                "col-start-1 md:col-start-0 order-2 md:order-1 col-span-10 md:col-span-2"
                              }
                            >
                              <PhoneInput
                                ref={phoneInputRef}
                                country={'in'}
                                value={values.students[index].mobile_number}
                                onChange={(value) => handlePhoneChange(value, index, setFieldValue)}
                                onKeyDown={(e) => handleKeyDown(e, phoneInputRef)}
                                inputProps={{
                                  name: `students.${index}.mobile_number`,
                                  required: true,
                                }}
                                containerClass="react-tel-input shadow rounded-lg"
                                inputClass="form-control"
                                buttonClass="flag-dropdown"
                                dropdownClass="country-list"
                                enableSearch
                                disableSearchIcon
                                placeholder="Mobile number"
                              />
                              <ErrorMessage
                                name={`students.${index}.mobile_number`}
                              >
                                {(msg) => (
                                  <div className={"text-red-500 text-xs"}>
                                    {msg}
                                  </div>
                                )}
                              </ErrorMessage>
                            </div>
                            <div
                              className={
                                "col-start-1 md:col-start-0 order-1 md:order-2 col-span-10 md:col-span-2"
                              }
                            >
                              <Field
                                type={"email"}
                                name={`students.${index}.email`}
                                wrapperClasses={"-mb-0"}
                                placeholder="Email Id"
                                component={Fields.FieldInput}
                              />
                              <ErrorMessage name={`students.${index}.email`}>
                                {(msg) => (
                                  <div className={"text-red-500 text-xs"}>
                                    {msg}
                                  </div>
                                )}
                              </ErrorMessage>
                            </div>
                            {isEdit && (
                              <div
                                className={
                                  "col-start-1 col-span-10 sm:col-span-3 md:col-span-1 order-4 md:order-3 h-12 bg-white shadow rounded-lg md:flex justify-center items-center text-md mb-0 col-auto cursor-pointer"
                                }
                                onClick={() => openModal(student)}
                              >
                                <span className="icon icon-edit-2 text-zinc-400 text-xl"></span>
                              </div>
                            )}
                          </div>
                        </Transition>
                      ))}
                    </div>
                    <button
                      type={"button"}
                      className={"mt-2"}
                      onClick={() => addRow(arrayHelpers)}
                    >
                      <i className="icon icon-plus-circle text-2xl"></i>
                    </button>
                  </>
                );
              }}
            />

            <button
              disabled={isSubmitting || isLoading}
              className="bg-black w-full md:w-4/5 block hover:bg-slate-600 text-white font-bold my-4 mx-auto flex justify-center py-3 px-4 rounded-lg focus:outline-none focus:shadow-outline"
              type="submit"
            >
              {isLoading && <Partials.PartialLoading />}
              {isEdit ? "Save changes" : "Add to the class"}
            </button>
          </form>
        )}
      </Formik>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-10 overflow-y-auto"
          onClose={closeModal}
        >
          <div className="min-h-screen px-4 text-center">
            <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
            <span
              className="inline-block h-screen align-middle"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <div className="inline-block w-full max-w-xl p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl" style={{ minHeight: '280px' }}>
                <Dialog.Title
                  as="h3"
                  className="text-xl font-['Poppins-Bold'] leading-6 text-gray-900"
                >
                  Edit student details
                </Dialog.Title>
                <div className="mt-2">
                  <Formik
                    initialValues={editingStudent}
                    validationSchema={UTILS.forms.editStudentDetailsSchema}
                    onSubmit={handleModalSubmit}
                  >
                    {({ values, handleSubmit, isSubmitting, setFieldValue }) => (
                      <form onSubmit={handleSubmit} className="pt-2 md:pt-6">
                        <div
                          key={"add-students-to-class"}
                          className="grid grid-cols-10 gap-1 md:gap-2 mb-4 border-b lg:border-b-0"
                        >
                          <div
                            className={
                              "col-start-1 md:col-start-0 col-span-10 sm:col-span-5 md:col-span-2"
                            }
                          >
                            <Field
                              type={"text"}
                              name={`first_name`}
                              wrapperClasses={"mb-0"}
                              placeholder="First Name"
                              component={Fields.FieldInput}
                            />
                          </div>
                          <div
                            className={
                              "col-start-1 md:col-start-0 col-span-10 sm:col-span-5 md:col-span-2"
                            }
                          >
                            <Field
                              type={"text"}
                              name={`last_name`}
                              wrapperClasses={"mb-0"}
                              placeholder="Last Name"
                              component={Fields.FieldInput}
                            />
                          </div>
                          <div
                            className={
                              "col-start-1 md:col-start-0 order-2 lg:order-1 col-span-10 md:col-span-3"
                            }
                          >
                            <PhoneInput
                              ref={modalPhoneInputRef}
                              country={'in'}
                              value={values.mobile_number}
                              onChange={(value) => setFieldValue('mobile_number', value)}
                              onKeyDown={(e) => handleKeyDown(e, modalPhoneInputRef)}
                              inputProps={{
                                name: 'mobile_number',
                                required: true,
                              }}
                              containerClass="react-tel-input modal-phone-input shadow rounded-lg"
                              inputClass="form-control"
                              buttonClass="flag-dropdown"
                              dropdownClass="country-list"
                              enableSearch
                              disableSearchIcon
                              placeholder="Mobile number"
                            />
                          </div>
                          <div
                            className={
                              "col-start-1 md:col-start-0 order-1 lg:order-2 col-span-10 md:col-span-3"
                            }
                          >
                            <Field
                              type={"email"}
                              name={`email`}
                              wrapperClasses={"mb-0"}
                              placeholder="Email Id"
                              component={Fields.FieldInput}
                            />
                          </div>
                        </div>
                        <div className="mt-4 flex justify-end">
                          <button
                            type="button"
                            className="inline-flex justify-center px-8 py-2 text-sm font-medium text-zinc-900 bg-transparent border-2 border border-zinc-200 rounded-md hover:bg-zinc-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-zinc-500"
                            onClick={closeModal}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="ml-2 inline-flex justify-center px-8 py-2 text-sm font-medium text-white bg-black border border-transparent rounded-md hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-zinc-500"
                          >
                            Save
                          </button>
                        </div>
                      </form>
                    )}
                  </Formik>
                </div>
              </div>
            </Transition.Child>
          </div>
        </Dialog>
      </Transition>
    </>
  );
});

BlockAddStudentsToClass.propTypes = {
  initialData: PropTypes.arrayOf(
    PropTypes.shape({
      first_name: PropTypes.string,
      last_name: PropTypes.string,
      email: PropTypes.string,
      mobile_number: PropTypes.string,
    })
  ),
  isEdit: PropTypes.bool,
};

BlockAddStudentsToClass.defaultProps = {
  initialData: defaultValue,
};

export default BlockAddStudentsToClass;